<template>
  <slot></slot>
  <modal :id="modalId" :title="modalTitle" @shown="init">

    <!-- Begin of "MAIN" mode. -->
    <div v-if="mode === 'main'">

      <!-- Smartlist "create new" or "load existing" selector -->
      <div v-if="smartlistIdOptions.length > 0" class="form-group row my-3">
        <label for="id" class="col-form-label col-sm-2">Smartlist:</label>
        <div class="col-sm-10">
          <select v-model="id" class="form-control form-control-sm">
            <option v-for="list in smartlistIdOptionsWithNew" :value="list.id">{{ list.name }}</option>
          </select>
        </div>
      </div>

      <!-- Smartlist name field -->
      <div v-if="!existingSmartlist" class="form-group row my-3">
        <label for="name" class="col-form-label col-sm-2">Name:</label>
        <div class="col-sm-10">
          <input v-model="name" type="text" class="form-control form-control-sm" placeholder="Enter a smartlist name...">
        </div>
      </div>

      <!-- Archived deployment campaign warning -->
      <div v-if="showArchivedSmartlistWarning" class="alert alert-danger mb-0 text-center">
        The selected smartlist is archived and not available for updating.
      </div>

    </div>
    <!-- End of "MAIN" mode. -->

    <!-- Begin of "CONFIRM" mode. -->
    <div v-if="mode === 'confirm'">

      <div class="alert alert-warning text-center mt-2">
        <span v-if="existingSmartlist">
          Are you sure you want to add <strong>{{ adjusterIds.length }}</strong> adjusters to the "{{ existingSmartlist.name }}" smartlist?
        </span>
        <span v-else>
          Are you sure you want to create "{{ name }}" smartlist with <strong>{{ adjusterIds.length }}</strong> adjusters?
        </span>
      </div>

      <div v-if="error" class="alert alert-danger text-center mt-2">
        {{ error }}
      </div>

    </div>
    <!-- End of "CONFIRM" mode. -->

    <!-- Begin of "SUCCESS" mode. -->
    <div v-if="mode === 'success'">
      <div class="alert alert-success text-center mt-2">
        <h3>
          <i class="fa fa-check-circle fa-spin"></i>
          The operation is successful. Redirecting to smartlist page...
        </h3>
      </div>
    </div>
    <!-- End of "SUCCESS" mode. -->

    <template v-slot:footer>
      <button type="button" class="btn btn-default" @click="hideModal"><i class="fa fa-fw fa-ban"></i> Cancel</button>

      <template v-if="mode === 'main'">
        <button type="button" class="btn btn-secondary" :disabled="!proceedButtonAvailable" @click="mode = 'confirm'">
          <i class="fa fa-fw fa-arrow-right"></i> Proceed
        </button>
      </template>

      <template v-if="mode === 'confirm'">
        <button type="button" class="btn btn-secondary" @click="mode = 'main'"><i class="fa fa-fw fa-arrow-left"></i> Back</button>
        <button type="button" class="btn btn-success" @click="submitData()" :disabled="processingSubmit">
          <i class="fa fa-fw fa-save"></i> Confirm
          <i class="fa fa-spinner fa-spin" v-if="processingSubmit"></i>
        </button>
      </template>

      <template v-if="mode === 'success'">
        <button type="button" class="btn btn-default" @click="hideModal"><i class="fa fa-fw fa-ban"></i> Close</button>
      </template>
    </template>

  </modal>
</template>

<script>
  import HasModal from "../mixins/HasModal";
  import { mapState } from 'pinia'
  import { useCheckboxListStore } from "../../stores/checkboxList"

  export default {
    mixins: [HasModal],

    props: {

      action: {
        type: String,
        required: true,
      },

      modalId: {
        type: String,
        default: 'smartlistModal'
      },

      existingSmartlists: {
        type: Array,
        required: true,
      },

    },

    /**
     * Variables (models).
     */
    data () {
      return {

        modalTitle: 'Add to Smartlist',
        mode: 'main',
        processingSubmit: false,
        error: '',

        // Form fields.
        id: 0,
        name: '',
      }
    },

    /**
     * Computed values
     */
    computed: {
      ...mapState(useCheckboxListStore, ['checkedIds']),

      adjusterIds() {
        return this.checkedIds();
      },

      smartlistIdOptions() {
        return this.existingSmartlists.map(list => {
          return {'id': list.id, 'name': list.name}
        });
      },

      smartlistIdOptionsWithNew() {
        return [{"id": 0, "name": "New"}, ...this.smartlistIdOptions];
      },

      existingSmartlist() {
        return this.existingSmartlists.find(list => parseInt(list.id) === this.id);
      },

      showArchivedSmartlistWarning() {
        return this.existingSmartlist && !this.existingSmartlist.status;
      },

      proceedButtonAvailable() {
        // Block archived smartlists.
        if (this.showArchivedSmartlistWarning) return false;
        if (this.existingSmartlist) {
          // Allow all existing smartlists.
          return true;
        } else {
          // If smartlist is new, block empty name.
          return this.name;
        }
      },

    },

    /**
     * Functions (methods).
     */
    methods: {

      init() {
        this.mode = 'main';
        this.id = 0;
        this.name = '';
        this.error = '';
      },

      submitData() {
        let formData = this.formData();
        this.processingSubmit = true;
        axios.post(this.action, formData, formData)
          .then(response => {
            this.mode = 'success';
            window.location.href = response.data.redirect;
          })
          .catch(error =>{
            this.handleErrors(error, 'Unable to process request.')
          })
          .then(() =>{
            this.processingSubmit = false;
          });
      },

      formData() {
        const formData = new FormData();
        formData.append('_token', this.$csrf_token);
        this.adjusterIds.forEach(adjusterId => formData.append('adjuster_ids[]', adjusterId));
        formData.append('name', this.existingSmartlist ? this.existingSmartlist.name : this.name);
        if (this.existingSmartlist) formData.append('id', this.existingSmartlist.id);
        return formData;
      },

      handleErrors(errorObj, defaultMessage) {
        if (errorObj.response.status === 422) {
          let errors = errorObj.response.data.errors || {};
          let errorMessages = [];
          Object.keys(errors).forEach(field => {
            errorMessages.push(errors[field]);
          })
          this.error = errorMessages.join(' ');
        } else {
          this.error = defaultMessage;
        }
      },

    },
  }
</script>
